import { FC } from 'react';

import { ArrowLeftOutline, ArrowRightOutline, Box, Button, CheckOutline, HStack } from '@bonusly/design-system';

interface SpotlightNavigationProps {
	currentIndex: number;
	totalCards: number;
	onPrevious: () => void;
	onNext: () => void;
	onGoToCard: (index: number) => void;
}

export const SpotlightNavigation: FC<SpotlightNavigationProps> = ({ currentIndex, totalCards, onPrevious, onNext, onGoToCard }) => {
	const isFirst = currentIndex === 0;
	const isLast = currentIndex === totalCards - 1;

	return (
		<Box
			position={{ base: 'fixed', md: 'relative' }}
			bottom={{ base: 0, md: 'auto' }}
			left={0}
			right={0}
			width="100%"
			maxW={{ base: '100%', md: '900px' }}
			mx="auto"
			mt={{ base: 0, md: 8 }}
			mb={{ base: 0, md: 0 }}
			pb={{ base: 4, md: 0 }}
			pt={{ base: 4, md: 0 }}
			bg="transparent"
			backdropFilter={{ base: 'blur(10px)', md: 'none' }}
			display="flex"
			justifyContent="space-between"
			alignItems="center"
			zIndex={50}
		>
			{/* Mobile: Just buttons with equal padding */}
			<Box display={{ base: 'flex', md: 'none' }} width="100%" justifyContent="space-between" px={4}>
				<Button
					leftIcon={<ArrowLeftOutline size="sm" />}
					variant="ghost"
					onClick={onPrevious}
					disabled={isFirst}
					px={4}
					py={2}
					color="text.soft"
					fontSize={{ base: 'xs', md: 'sm' }}
					_hover={{
						bg: 'none',
					}}
					_active={{
						bg: 'none',
					}}
				>
					Back
				</Button>

				<Button
					rightIcon={isLast ? <CheckOutline size="sm" /> : <ArrowRightOutline size="sm" />}
					variant="solid"
					onClick={onNext}
					disabled={isLast}
					bg="secondary.bg"
					color="text.onColor"
					px={4}
					py={2}
					h="auto"
					fontSize={{ base: 'xs', md: 'sm' }}
					_hover={{
						bg: 'secondary.bgHover',
						color: 'text.onColor',
					}}
					_active={{
						bg: 'secondary.bgActive',
						color: 'text.onColor',
					}}
				>
					{isLast ? 'Share' : 'Next'}
				</Button>
			</Box>

			{/* Desktop: Buttons with pills in middle */}
			<Box display={{ base: 'none', md: 'flex' }} width="100%" justifyContent="space-between" alignItems="center">
				<Button
					leftIcon={<ArrowLeftOutline size="sm" />}
					variant="ghost"
					onClick={onPrevious}
					disabled={isFirst}
					px="20px"
					py="10px"
					color="text.soft"
					_hover={{
						bg: 'none',
					}}
					_active={{
						bg: 'none',
					}}
				>
					Back
				</Button>

				<HStack spacing={2}>
					{Array.from({ length: totalCards }).map((_, index) => (
						<Box
							key={index}
							as="button"
							w={index === currentIndex ? '40px' : '14px'}
							h="14px"
							borderRadius="20px"
							bg={index === currentIndex ? 'text.default' : 'gray.200'}
							cursor="pointer"
							transition="all 0.2s ease"
							onClick={() => onGoToCard(index)}
							_hover={{
								bg: index === currentIndex ? 'text.default' : 'gray.300',
							}}
						/>
					))}
				</HStack>

				<Button
					rightIcon={isLast ? <CheckOutline size="sm" /> : <ArrowRightOutline size="sm" />}
					variant="solid"
					onClick={onNext}
					disabled={isLast}
					bg="secondary.bg"
					color="text.onColor"
					px="20px"
					py="10px"
					h="auto"
					fontSize="sm"
					_hover={{
						bg: 'secondary.bgHover',
						color: 'text.onColor',
					}}
					_active={{
						bg: 'secondary.bgActive',
						color: 'text.onColor',
					}}
				>
					{isLast ? 'Share' : 'Next'}
				</Button>
			</Box>
		</Box>
	);
};
